// frontend/src/app/analytics.ts
// Web-Analytics + Google-Ads-Conversion fuer die SPA.
//
// Warum es diese Datei gibt: Mit dem WEB_SPA-Cutover (v7.1.1.14) sind zwei
// Dinge ersatzlos entfallen, die die Legacy-Vanilla-Web noch hatte —
// public/js/tracking.js (eigenes, cookiefreies Pageview-Tracking gegen
// /api/tracking, Server-Route lebt unveraendert weiter) und
// public/js/cookie-consent.js (Consent Mode v2 + Google Ads). Seitdem erzeugen
// Web-Besucher NULL Pageviews im Admin-Tracking und laufende Ads-Kampagnen auf
// app.lorath.eu sind conversion-blind. Das hier ist der Port beider Schichten.
//
// Zwei streng getrennte Ebenen — beide unter STRIKTEM Opt-in fuer ALLE
// Besucher (P15, Art. 4 Nr. 11 DSGVO / § 25 TDDDG):
//   1. Eigenes Pageview-Tracking — cookiefrei (Session-ID nur in sessionStorage),
//      keine IP-Speicherung serverseitig. Startet AUSSCHLIESSLICH nach
//      ausdruecklicher Einwilligung (Consent-Cookie 'accepted') — fuer Gaeste
//      UND eingeloggte Nutzer gleichermassen. Die fruehere Annahme, die
//      AGB-Zustimmung decke das eigene Tracking eingeloggter Nutzer ab, war
//      rechtlich KEINE Einwilligung und ist ersatzlos entfallen.
//   2. Google Ads (gtag) — IMMER nur nach ausdruecklicher Zustimmung, mit
//      Consent Mode v2 (Defaults auf 'denied', erst danach 'update').
//
// NIEMALS in der nativen App: Google Ads im App-Wrapper ist ein
// App-Review-Blocker (die Legacy loeste das ueber die 'apple-app'-Klasse).
// Hier ist das Gate isCapacitorNativeSync() — die einzige verlaessliche
// "echte App"-Erkennung (platform.os steht im mobilen Browser ebenfalls auf
// 'ios'/'android').
//
// ABGRENZUNG (bewusst NICHT von diesem Consent-Gate erfasst): Der PII-freie,
// server-generierte Demo-Trichter der Gastfläche (P11 „Weltenschwelle",
// server/demo-chat.js + Tabelle demo_sessions) laeuft REIN SERVERSEITIG ohne
// jeden Endgeraetezugriff — kein Cookie, kein localStorage, kein
// sessionStorage, keine gespeicherte IP. Er faellt damit NICHT unter § 25
// TDDDG und braucht KEINE Einwilligung; er ist von diesem Client-seitigen
// Consent-Gate voellig UNBERUEHRT.

import { isCapacitorNativeSync } from '../native/bridge';

// Google-Ads-Conversion-ID, uebernommen aus public/js/cookie-consent.js.
const GTAG_ID = 'AW-18042562668';
const CONSENT_COOKIE = 'cookie_consent';
const SESSION_KEY = 'ff_session_id';
const CONSENT_EVENT = 'lorath:consent-changed';

export type ConsentValue = 'accepted' | 'rejected' | null;

interface GtagWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}

function win(): GtagWindow | null {
  return typeof window === 'undefined' ? null : (window as GtagWindow);
}

/** Analytics laeuft ausschliesslich im Browser — in der App ist alles ein No-op. */
function analyticsEnabled(): boolean {
  return typeof window !== 'undefined' && !isCapacitorNativeSync();
}

// ─── Consent-Zustand ────────────────────────────────────────────────────────

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

function writeCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax`;
}

export function getConsent(): ConsentValue {
  const raw = readCookie(CONSENT_COOKIE);
  return raw === 'accepted' || raw === 'rejected' ? raw : null;
}

/**
 * Zustimmung setzen. 'accepted' schaltet Google Ads scharf UND startet das
 * eigene Pageview-Tracking; 'rejected' laesst beides aus.
 * Feuert ein DOM-Event, damit gemountete Komponenten (Banner) reagieren, ohne
 * dass wir einen weiteren globalen Store brauchen.
 */
export function setConsent(value: 'accepted' | 'rejected'): void {
  if (!analyticsEnabled()) return;
  writeCookie(CONSENT_COOKIE, value, 365);
  if (value === 'accepted') {
    loadGtag();
    grantGtagConsent();
    startPageviewTracking();
  }
  win()?.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
}

export function subscribeConsent(listener: (value: ConsentValue) => void): () => void {
  const w = win();
  if (!w) return () => undefined;
  const handler = (event: Event) => listener((event as CustomEvent<ConsentValue>).detail ?? getConsent());
  w.addEventListener(CONSENT_EVENT, handler);
  return () => w.removeEventListener(CONSENT_EVENT, handler);
}

// ─── Google Ads (Consent Mode v2) ───────────────────────────────────────────

let consentDefaultsSet = false;

/**
 * Consent-Mode-Defaults MUESSEN gesetzt sein, BEVOR gtag.js laedt — sonst
 * zaehlt der erste Treffer als uneingeschraenkte Einwilligung. Deshalb laeuft
 * das unabhaengig davon, ob je zugestimmt wird.
 */
function ensureConsentDefaults(): void {
  const w = win();
  if (!w || consentDefaultsSet) return;
  consentDefaultsSet = true;
  w.dataLayer = w.dataLayer ?? [];
  const dataLayer = w.dataLayer;
  // Bewusst eine echte Funktion (keine Arrow): gtag.js erwartet das ECHTE
  // arguments-Objekt im dataLayer — ein Array wird dort als GTM-Event gedeutet
  // und die Consent-Parameter kaemen nie an. Eine Arrow-Function haette
  // ausserdem gar kein eigenes `arguments`.
  function gtagRaw(): void {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  }
  const gtag = gtagRaw as unknown as (...args: unknown[]) => void;
  w.gtag = gtag;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  });
}

function loadGtag(): void {
  const w = win();
  if (!w || !analyticsEnabled()) return;
  if (typeof document === 'undefined' || document.getElementById('gtag-script')) return;
  ensureConsentDefaults();

  const script = document.createElement('script');
  script.id = 'gtag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
  document.head.appendChild(script);

  w.gtag?.('js', new Date());
  w.gtag?.('config', GTAG_ID);
}

function grantGtagConsent(): void {
  win()?.gtag?.('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });
}

/**
 * Kauf-Conversion an Google Ads melden. No-op ohne Zustimmung und in der App.
 * Wird vom Rueckkehr-Handler der Coins-Seite nach bestaetigter Zahlung
 * aufgerufen — bewusst erst NACH der Server-Verifikation, damit kein
 * abgebrochener Checkout als Conversion zaehlt.
 */
export function trackPurchaseConversion(valueEur: number, orderId?: string): void {
  if (!analyticsEnabled() || getConsent() !== 'accepted') return;
  if (!Number.isFinite(valueEur) || valueEur <= 0) return;
  win()?.gtag?.('event', 'conversion', {
    send_to: GTAG_ID,
    value: valueEur,
    currency: 'EUR',
    transaction_id: orderId,
  });
}

// ─── Eigenes Pageview-Tracking (cookiefrei) ─────────────────────────────────

let trackingActive = false;
let sessionId: string | null = null;
let currentPageviewId: number | string | null = null;
let pageStartedAt: number | null = null;

function getSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const stored = window.sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      sessionId = stored;
      return stored;
    }
  } catch {
    // Privater Modus: dann eben eine fluechtige ID pro Seitenaufruf.
  }
  const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  sessionId = generated;
  try {
    window.sessionStorage.setItem(SESSION_KEY, generated);
  } catch {
    // s.o. — best effort.
  }
  return generated;
}

/**
 * Verweildauer der vorigen Seite nachreichen. sendBeacon ueberlebt den
 * Seitenwechsel/Tab-Schluss, fetch-keepalive ist der Fallback.
 */
function flushDuration(): void {
  if (currentPageviewId === null || pageStartedAt === null) return;
  const seconds = Math.round((performance.now() - pageStartedAt) / 1000);
  const id = currentPageviewId;
  currentPageviewId = null;
  pageStartedAt = null;
  if (seconds <= 0) return;

  const url = `/api/tracking/pageview/${encodeURIComponent(String(id))}/duration`;
  const payload = JSON.stringify({ duration_seconds: seconds });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    }
    void fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Fire-and-forget: Tracking darf NIE eine Navigation stoeren.
  }
}

/** Einen Seitenaufruf melden. Fire-and-forget, Fehler bleiben folgenlos. */
export function trackPageview(path: string): void {
  if (!trackingActive || !analyticsEnabled()) return;
  flushDuration();
  pageStartedAt = performance.now();

  void fetch('/api/tracking/pageview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      session_id: getSessionId(),
      path,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth,
      source: 'app',
    }),
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((data: { pageview_id?: number | string } | null) => {
      if (data?.pageview_id !== undefined) currentPageviewId = data.pageview_id;
    })
    .catch(() => undefined);
}

let unloadHooked = false;

export function startPageviewTracking(): void {
  if (trackingActive || !analyticsEnabled()) return;
  trackingActive = true;
  if (!unloadHooked && typeof window !== 'undefined') {
    unloadHooked = true;
    // pagehide statt unload: unload verhindert den bfcache und feuert auf iOS
    // Safari unzuverlaessig.
    window.addEventListener('pagehide', flushDuration);
  }
}

export function isPageviewTrackingActive(): boolean {
  return trackingActive;
}

/**
 * Beim Boot aufrufen. Setzt die Consent-Defaults (Pflicht vor jedem
 * gtag-Treffer — sendet selbst nichts) und aktiviert NUR, was eine
 * ausdrueckliche Einwilligung erlaubt.
 *
 * Striktes Opt-in fuer ALLE (P15): Ohne Entscheidung ODER bei 'rejected' laeuft
 * WEDER das eigene Pageview-Tracking NOCH Google Ads NOCH irgendein anderes
 * Client-Analytics-Event. Es gibt keinen Login-Sonderfall mehr — die frueher
 * angenommene AGB-Deckung fuer eingeloggte Nutzer war rechtlich keine
 * Einwilligung.
 */
export function initAnalytics(): void {
  if (!analyticsEnabled()) return;
  ensureConsentDefaults();

  if (getConsent() === 'accepted') {
    loadGtag();
    grantGtagConsent();
    startPageviewTracking();
  }
  // Fehlende oder abgelehnte Entscheidung: bewusst nichts weiter tun.
}
